import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Folder, Clock, User, MapPin, Calendar, Film, Play, Pause,
  FileText, Music, Download, Camera, Mic, ChevronLeft, ChevronRight, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SharedStoryboardFolder = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [fullscreenShot, setFullscreenShot] = useState(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchFolder = async () => {
      try {
        const res = await fetch(`${API_URL}/api/moodboard/folders/shared/${shareToken}`);
        if (res.ok) {
          const result = await res.json();
          setData(result);
        } else {
          setError('Storyboard folder not found or link has expired.');
        }
      } catch (e) {
        setError('Failed to load storyboard folder.');
      }
      setLoading(false);
    };
    
    if (shareToken) {
      fetchFolder();
    }
  }, [shareToken]);

  const toggleMusicPlayback = (music) => {
    if (currentlyPlaying === music.id) {
      setCurrentlyPlaying(null);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } else {
      setCurrentlyPlaying(music.id);
      if (audioRef.current) {
        audioRef.current.src = music.url;
        audioRef.current.play().catch(err => console.error('Playback failed:', err));
      }
    }
  };

  const openFullscreen = (shot, index) => {
    setFullscreenShot({ shot, index });
  };

  const closeFullscreen = () => {
    setFullscreenShot(null);
  };

  const navigateFullscreen = (direction) => {
    if (!fullscreenShot || !data?.shots) return;
    const newIndex = direction === 'prev' 
      ? Math.max(0, fullscreenShot.index - 1)
      : Math.min(data.shots.length - 1, fullscreenShot.index + 1);
    setFullscreenShot({ shot: data.shots[newIndex], index: newIndex });
  };

  // Keyboard navigation for fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!fullscreenShot) return;
      if (e.key === 'Escape') closeFullscreen();
      if (e.key === 'ArrowLeft') navigateFullscreen('prev');
      if (e.key === 'ArrowRight') navigateFullscreen('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenShot, data?.shots]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Folder size={48} className="mx-auto text-white/30 mb-4" />
          <h1 className="text-white text-xl font-semibold mb-2">Storyboard Not Found</h1>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  const { folder, shots, project } = data;

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Storyboard</p>
          <h1 className="text-white text-3xl font-bold uppercase tracking-wide">
            {folder.name || 'Untitled Folder'}
          </h1>
          {project && (
            <p className="text-white/60 mt-2">Project: {project.name}</p>
          )}
        </div>

        {/* Folder Details Card */}
        <div className="border border-white/30 rounded-lg bg-black/50 p-6 mb-8">
          <div className="flex gap-6">
            {/* Cover Image */}
            {folder.cover_image && (
              <div className="flex-shrink-0">
                <img 
                  src={folder.cover_image} 
                  alt={folder.name}
                  className="w-40 h-40 object-cover rounded-lg border border-white/20"
                />
              </div>
            )}
            
            <div className="flex-1">
              {/* Meta Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {folder.client && (
              <div>
                <div className="flex items-center gap-1.5 text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">
                  <User size={12} />
                  Client
                </div>
                <div className="text-white font-medium">{folder.client}</div>
              </div>
            )}
            {folder.director && (
              <div>
                <div className="flex items-center gap-1.5 text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">
                  <Film size={12} />
                  Director
                </div>
                <div className="text-white font-medium">{folder.director}</div>
              </div>
            )}
            {folder.shoot_date && (
              <div>
                <div className="flex items-center gap-1.5 text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">
                  <Calendar size={12} />
                  Shoot Date
                </div>
                <div className="text-white font-medium">{folder.shoot_date}</div>
              </div>
            )}
            {folder.location && (
              <div>
                <div className="flex items-center gap-1.5 text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">
                  <MapPin size={12} />
                  Location
                </div>
                <div className="text-white font-medium">{folder.location}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {folder.description && (
            <p className="text-white/70 mb-4">{folder.description}</p>
          )}

          {/* Notes */}
          {folder.notes && (
            <p className="text-white/50 text-sm italic border-l-2 border-white/20 pl-3 mb-4">{folder.notes}</p>
          )}

          {/* Documents */}
          {folder.documents && folder.documents.length > 0 && (
            <div className="mb-4">
              <h3 className="text-white/60 text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                <FileText size={12} />
                Documents ({folder.documents.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {folder.documents.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 text-blue-300 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                  >
                    <FileText size={14} />
                    <span className="truncate max-w-[150px]">{doc.name}</span>
                    <Download size={12} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Music Files */}
          {folder.music_files && folder.music_files.length > 0 && (
            <div>
              <h3 className="text-white/60 text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                <Music size={12} />
                Music ({folder.music_files.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {folder.music_files.map((music) => (
                  <button
                    key={music.id}
                    onClick={() => toggleMusicPlayback(music)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      currentlyPlaying === music.id 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                    }`}
                  >
                    {currentlyPlaying === music.id ? <Pause size={14} /> : <Play size={14} />}
                    <span className="truncate max-w-[150px]">{music.name}</span>
                  </button>
                ))}
              </div>
              <audio ref={audioRef} onEnded={() => setCurrentlyPlaying(null)} className="hidden" />
            </div>
          )}
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center justify-between mb-6 px-2">
          <h2 className="text-white text-lg font-semibold">Shots</h2>
          <div className="text-white/50 text-sm">{shots.length} {shots.length === 1 ? 'shot' : 'shots'}</div>
        </div>

        {/* Shots Grid */}
        {shots.length === 0 ? (
          <div className="text-center py-16">
            <Film size={48} className="mx-auto text-white/20 mb-4" />
            <p className="text-white/40">No shots in this storyboard yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {shots.map((shot, index) => (
              <div 
                key={shot.id} 
                className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-white/30 transition-colors cursor-pointer"
                onClick={() => openFullscreen(shot, index)}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-black relative">
                  {shot.video_url ? (
                    <>
                      <video 
                        src={shot.video_url}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Play size={20} className="text-white ml-0.5" fill="white" />
                        </div>
                      </div>
                    </>
                  ) : shot.thumbnail_url ? (
                    <img 
                      src={shot.thumbnail_url} 
                      alt={`Shot ${shot.shot_number}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film size={32} className="text-white/20" />
                    </div>
                  )}
                  
                  {/* Shot type badge */}
                  <div className="absolute top-2 right-2 bg-black/60 rounded px-2 py-1">
                    <span className="text-white/80 text-xs">{shot.shot_type || 'Shot'}</span>
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <h4 className="text-white font-semibold mb-2">
                    SCENE {shot.scene_number || 1}: <span className="font-normal text-white/70">Shot {shot.shot_number || 1}</span>
                  </h4>
                  
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-white/60">
                      <Clock size={12} />
                      <span>{shot.duration_start || '00:00'} → {shot.duration_end || '00:00'}</span>
                    </div>
                    
                    {shot.character_name && (
                      <div className="flex items-center gap-2 text-white/60">
                        <User size={12} />
                        <span>{shot.character_name}</span>
                      </div>
                    )}
                    
                    {shot.dialogue && (
                      <div className="flex items-start gap-2 text-white/60">
                        <Mic size={12} className="mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2 italic">"{shot.dialogue}"</span>
                      </div>
                    )}
                    
                    {shot.camera_movement && (
                      <div className="flex items-start gap-2 text-white/60">
                        <Camera size={12} className="mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-1">{shot.camera_movement}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fullscreen Viewer */}
        {fullscreenShot && (
          <div 
            className="fixed inset-0 bg-black z-50 flex items-center justify-center"
            onClick={closeFullscreen}
          >
            {/* Close button */}
            <button
              onClick={closeFullscreen}
              className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X size={24} className="text-white" />
            </button>

            {/* Shot info */}
            <div className="absolute top-4 left-4 z-50 bg-black/60 rounded-lg px-4 py-2">
              <p className="text-white font-semibold">
                Scene {fullscreenShot.shot.scene_number || 1}: Shot {fullscreenShot.shot.shot_number || 1}
              </p>
              <p className="text-white/60 text-sm">{fullscreenShot.shot.shot_type || 'Shot'}</p>
              <p className="text-white/40 text-xs mt-1">{fullscreenShot.index + 1} of {shots.length}</p>
            </div>

            {/* Previous arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateFullscreen('prev');
              }}
              disabled={fullscreenShot.index === 0}
              className={`absolute left-4 top-1/2 -translate-y-1/2 z-50 p-4 rounded-full transition-all ${
                fullscreenShot.index > 0 
                  ? 'bg-white/10 hover:bg-white/30 cursor-pointer' 
                  : 'bg-white/5 cursor-not-allowed opacity-30'
              }`}
            >
              <ChevronLeft size={40} className="text-white" />
            </button>

            {/* Next arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateFullscreen('next');
              }}
              disabled={fullscreenShot.index === shots.length - 1}
              className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 p-4 rounded-full transition-all ${
                fullscreenShot.index < shots.length - 1 
                  ? 'bg-white/10 hover:bg-white/30 cursor-pointer' 
                  : 'bg-white/5 cursor-not-allowed opacity-30'
              }`}
            >
              <ChevronRight size={40} className="text-white" />
            </button>

            {/* Media content */}
            <div 
              className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {fullscreenShot.shot.video_url ? (
                <video
                  ref={videoRef}
                  src={fullscreenShot.shot.video_url}
                  className="max-w-[90vw] max-h-[85vh] rounded-lg"
                  controls
                  autoPlay
                  playsInline
                />
              ) : fullscreenShot.shot.thumbnail_url ? (
                <img
                  src={fullscreenShot.shot.thumbnail_url}
                  alt={`Shot ${fullscreenShot.shot.shot_number}`}
                  className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
                />
              ) : (
                <div className="w-96 h-64 flex items-center justify-center bg-white/5 rounded-xl">
                  <Film size={64} className="text-white/20" />
                </div>
              )}
            </div>

            {/* Keyboard hints */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 text-white/40 text-sm">
              <span>← → Navigate</span>
              <span>•</span>
              <span>Esc Close</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <p className="text-white/30 text-xs">
            Shared via CREATRVERSE
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedStoryboardFolder;
